// Persistent tenant brand masthead — thin gold strip with the tenant's
// wordmark that sits at the top of every tenant-portal page (Dashboard,
// Coaching Records, Calls, KBs, Integrations, Branding, etc.).
//
// Reuses the exact `.brand-banner` class + `--brand-banner-color` /
// `--brand-banner-image` CSS vars already wired by `applyTenantTheme`;
// no second implementation. Reads the wordmark URL from the
// `data-brand-banner-url` dataset attribute the same way TfvfShell did.
//
// Fallback: if a domain has NEITHER `custom_banner_color` NOR
// `custom_banner_url` set (ipvsyabbit, fresh tenants, master admin
// preview) this component renders `null` — zero height, zero visual
// change. Guaranteed no regression for non-configured tenants.
//
// Sizing: intentionally slim (56px desktop / 48px mobile). This is a
// masthead — NOT a repeat of the Training page's tall hero. The
// wordmark caps at 36-40px so it breathes without shoving page content.
import React from 'react';

export const TenantBrandMasthead = () => {
  // Read the tenant branding state from the <html> dataset / inline CSS
  // vars set by `applyTenantTheme`. Same source of truth TfvfShell used —
  // no extra fetches, no context dependency.
  const bannerUrl =
    typeof document !== 'undefined'
      ? document.documentElement.dataset.brandBannerUrl || ''
      : '';
  const bannerColor =
    typeof document !== 'undefined'
      ? (document.documentElement.style.getPropertyValue('--brand-banner-color') || '').trim()
      : '';
  const hasBanner = !!(bannerUrl || bannerColor);

  if (!hasBanner) return null;

  return (
    <div
      className="flex items-center justify-center px-4 sm:px-6"
      style={{
        // The generic `.brand-banner` utility class ALSO paints the
        // wordmark as a `background-image: url(...); background-size:
        // cover` — that's designed for the training hero, where the
        // image should tile-fill a tall block. On this masthead we
        // render the wordmark as an <img> at a controlled size, so
        // we suppress the background image and keep only the colour.
        // Otherwise the two collide: the CSS background scales the
        // wordmark to cover 1664×N and clips it, masking the
        // properly-sized <img> underneath.
        backgroundColor: 'var(--brand-banner-color, var(--brand-accent))',
        backgroundImage: 'none',
        minHeight: 128,
      }}
      data-testid="tenant-brand-masthead"
    >
      {bannerUrl && (
        <img
          src={bannerUrl}
          alt=""
          className="max-h-24 sm:max-h-28 w-auto object-contain"
          data-testid="tenant-brand-masthead-logo"
          /* Broken tenant URLs must never leave a placeholder icon on
             the strip — hide the img on error, let the coloured band
             stand on its own. Warn to console for diagnostics. */
          onError={(e) => {
            console.warn('[brand-masthead] logo failed to load:', bannerUrl);
            e.target.style.visibility = 'hidden';
          }}
        />
      )}
    </div>
  );
};

export default TenantBrandMasthead;
